import type { SortingState } from '@tanstack/react-table';
import { useCallback, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { SlideOut } from '~/components/SlideOut/SlideOut';
import { Spinner } from '~/components/Spinner';
import { ArtistHeader } from '~/modules/artist-roster/pages/artist-details/components/ArtistHeader/ArtistHeader';
import { TrackList } from '~/modules/artist-roster/pages/artist-details/pages/artist-tracks/components/TrackList/TrackList';
import { TrackFilterPanel } from '~/modules/artist-roster/pages/artist-details/pages/artist-tracks/components/TracksFilter/TrackFilterPanel';
import { TracksHeader } from '~/modules/artist-roster/pages/artist-details/pages/artist-tracks/components/TracksHeader';
import { useReleasesUrlParams } from '~/modules/artist-roster/pages/artist-details/pages/artist-tracks/hooks/useReleasesUrlParams';
import type { TracksFilters } from '~/modules/artist-roster/pages/artist-details/utils/types';
import type { ArtistDetails } from '~/modules/artist-roster/types';

export function ArtistTracksPage() {
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const { filters, setFilters, updateFilters } = useReleasesUrlParams();
  const { searchText } = filters;
  const { artistData } = useOutletContext<{ artistData: ArtistDetails }>();

  const [sortingState, setSortingState] = useState<SortingState>([
    {
      id: 'initialReleaseDate',
      desc: true,
    },
  ]);

  const handleApplyFilters = useCallback(
    (appliedFilters: TracksFilters) => {
      setFilters({
        searchText,
        releaseType: appliedFilters.releaseType,
        role: appliedFilters.role,
      });
    },
    [searchText, setFilters],
  );

  if (!artistData) {
    return (
      <div className="flex w-full items-center justify-center p-4xl">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <ArtistHeader {...{ artistData }}>
        <TracksHeader
          setIsFilterPanelOpen={setIsFilterPanelOpen}
          sortingState={sortingState}
          filters={filters}
          updateFilters={updateFilters}
          handleApplyFilters={handleApplyFilters}
        />
      </ArtistHeader>
      <TrackList
        uaid={artistData.uaid}
        artistData={artistData}
        columnSort={sortingState[0]}
        setSortingState={setSortingState}
      />
      <SlideOut open={isFilterPanelOpen} onOpenChange={setIsFilterPanelOpen}>
        <TrackFilterPanel
          originalFilters={filters}
          onApplyFilters={handleApplyFilters}
        />
      </SlideOut>
    </>
  );
}

ArtistTracksPage.displayName = 'ArtistTracksPage';
